import { takeLatest, put, call } from 'redux-saga/effects';
import { AnyAction } from 'redux';
import parse from 'parse-link-header';
import apiService from '../../services/apiService';
import { categoriesTypes, filterParams } from './types';
import { RESPONSE_STATUS, ORDER_TYPES } from '../../helpers/consts';
import { captureEvent } from '../../analytics';

import {
  searchFirstCategoriesRequestSuccess,
  searchCategoriesRequestSuccess,
  searchCategoriesRequestFailure,
  activateInactivateCategoriesSuccess,
  activateInactivateCategoriesFailure,
  saveCategoryRequestResponse,
} from './actions';

const stringfySearchParams = paramsObject => {
  const {
    text,
    names,
    startDate,
    endDate,
    active,
    sort,
  }: filterParams = paramsObject;
  const url = [
    `&text=${text ?? ''}`,
    `&ids=${[...names ?? []].map(category => category.value) ?? ''}`,
    `&start_date=${startDate ?? ''}`,
    `&end_date=${endDate ?? ''}`,
    `&active=${active ?? true}`,
    `&sort=${sort ?? ORDER_TYPES.ALPHABETICAL_ASCENDING}`,
  ].join('');

  return url;
};

export function* searchCategoriesRequest({
  payload,
}: AnyAction) {
  try {
    const { params, page } = payload;
    const stringfiedParams = stringfySearchParams(params);
    const url = `/form_categories?${stringfiedParams}&page=${page}&per_page=50`;

    const { headers, data } = yield call(apiService.get, url);
    const parsedLinkHeader = parse(headers.link);
    const totalCount = headers['x-total-count'];

    const pagination = { total: totalCount, links: parsedLinkHeader };
    yield put(
      page === 1
        ? searchFirstCategoriesRequestSuccess({
          payload: data,
          pagination,
        })
        : searchCategoriesRequestSuccess({
          payload: data,
          pagination,
        }),
    );

    return data;
  } catch (error) {
    yield put(searchCategoriesRequestFailure());
  }
};

export function* activateInactivateCategoriesRequest({
  payload,
}: AnyAction) {
  try {
    const url = '/form_categories/activate_inactivate';
    const { status, error } = yield call(apiService.patch, url, { ...payload });

    if (status === 200) {
      yield put(activateInactivateCategoriesSuccess());
    } else {
      yield put(activateInactivateCategoriesFailure());
    }
    captureEvent('activateInactivateCategories', {
      categories: payload.ids?.length,
      status: 'success',
      action: payload.active ? 'activate' : 'inactivate',
    });
  } catch (error) {
    captureEvent('activateInactivateCategories', {
      categories: payload.ids?.length,
      status: 'error',
      action: payload.active ? 'activate' : 'inactivate',
      error: (error as any)?.response?.data?.message,
    });
    yield put(activateInactivateCategoriesFailure());
  }
};

export function* saveCategoryRequest({
  payload,
}: AnyAction) {
  let saveError = null;
  try {
    const category = {
      id: payload.id ?? null,
      name: payload.name,
    };

    const url = `/form_categories${payload.id ? `/${payload.id}` : ''}`;
    const { data } = yield call(payload.id ? apiService.put : apiService.post, url, category);
    if (data.error) saveError = data.error;

    if (saveError) {
      yield put(saveCategoryRequestResponse({
        saveStatus: RESPONSE_STATUS.FAILURE,
        saveError,
      }));
    } else {
      yield put(saveCategoryRequestResponse({
        saveStatus: RESPONSE_STATUS.SUCCESS,
        saveError,
      }));
    }
    captureEvent('saveCategory', {
      status: saveError ? 'error' : 'success',
      kind: payload.id ? 'edit' : 'create',
      hasErrors: saveError,
    });
    return true;
  } catch (error) {
    captureEvent('saveCategory', {
      status: 'error',
      kind: payload.id ? 'edit' : 'create',
      error: (error as any)?.response?.data?.message,
    });
    yield put(saveCategoryRequestResponse({
      saveStatus: RESPONSE_STATUS.FAILURE,
      saveError,
    }));
  }
};

export default [
  takeLatest(
    categoriesTypes.SEARCH_CATEGORIES_REQUEST,
    searchCategoriesRequest,
  ),
  takeLatest(
    categoriesTypes.ACTIVATE_INACTIVATE_CATEGORIES_REQUEST,
    activateInactivateCategoriesRequest,
  ),
  takeLatest(
    categoriesTypes.SAVE_CATEGORY_REQUEST,
    saveCategoryRequest,
  ),
];